import { useState, useRef, useEffect } from 'react'
import { useClickOutside } from 'hooks/useClickOutside.hook'
import Icon from 'components/ui/icon/Icon'
import FieldLabel from '../field-label'
import './style.scss'
import { computedClass } from 'utils'
import FieldInput from '../field-input/input'


const getOptionValue = (option) => option?.ID ?? option

const getOptionText = (option) => option?.NAME ?? option

const normalizeSelectedValues = (value) => {
    if (Array.isArray(value)) {
        return value
    }

    if (value === undefined || value === null || value === '') {
        return []
    }

    return [value]
}

const isSameValue = (a, b) => {
    if (typeof a === 'object' || typeof b === 'object') {
        return a === b
    }

    return String(a) === String(b)
}

function FieldSelect(props) {
    let {
        value: initialValue,
        name,
        TITLE: label,
        UF_VALUE_LIST,
        VALUE_LIST = [],
        UF_MULTIPLICITY=false,
        register,
        required,
        placeholder,
        labelMark,
        setValue,
        validation = {},
    } = props

    const ref = useRef(null)
    const defaultValue = useRef(initialValue)
    const [value, setLocalValue] = useState(initialValue)
    const [activated, setActivated] = useState(false)
	const [search, setSearch] =useState('');
    const isMultiple = UF_MULTIPLICITY;
    const selectedValues = isMultiple ? normalizeSelectedValues(value) : []
    const sourceOptions = VALUE_LIST.length ? VALUE_LIST : UF_VALUE_LIST || []
    const option = sourceOptions.find((option) => isSameValue(getOptionValue(option), value))
    const selectedOptions = sourceOptions.filter((option) =>
        selectedValues.some((value) => isSameValue(value, getOptionValue(option)))
    )

	 const options = sourceOptions.filter((option) => {
        const optionText = String(getOptionText(option) || '').toLowerCase()
        return optionText.includes(search.toLowerCase())
    })


    const text = isMultiple
        ? selectedOptions.map(getOptionText).join(', ') || placeholder || 'Выберите из списка'
        : option?.NAME || option || placeholder || 'Выберите из списка'

    const { ...rest } = (register && register(name, validation)) || {}

    const onChangeHandler = (selectedValue) => {
        let updatedValue = selectedValue

        if (isMultiple) {
            const hasValue = selectedValues.some((value) => isSameValue(value, selectedValue))
            updatedValue = hasValue
                ? selectedValues.filter((value) => !isSameValue(value, selectedValue))
                : [...selectedValues, selectedValue]
        }

        setValue(name, updatedValue, { shouldValidate: true })
        setLocalValue(updatedValue)

        if (!isMultiple) {
            setActivated(false)
        }
    }

	 const onSearchHandler = ({target:{value}})=>{
		setSearch(value)
	 }

    const onOpen = () => setActivated(!activated)

    useClickOutside(ref, () => setActivated(false))

    const classes = computedClass({
        'field-select': true,
        'field-select--active': activated,
        'field-select--selected': isMultiple ? selectedOptions.length : option,
    })

    useEffect(() => {
        const onFormReset = () => {
            setLocalValue(defaultValue.current)
        }
        if (ref.current) {
            const form = ref.current.closest('form')
            form?.addEventListener('reset', onFormReset)
            return () => form?.removeEventListener('reset', onFormReset)
        }
    }, [ref])

    useEffect(() => {
        setLocalValue(initialValue)
    }, [initialValue])

    return (
        <>
            <input type="hidden" {...rest} />
            {label && (
                <FieldLabel label={label} required={required || rest.required} labelMark={labelMark}></FieldLabel>
            )}
            <div ref={ref} className={classes}>
                <div className="field-input">
                    <div className="field-select__header" onClick={onOpen}>
                        {text && <div className="field-select__value">{text}</div>}
                        <Icon className="field-select__icon" name="i-arrow-down"></Icon>
                    </div>
                </div>

                <div className={'field-select__list'}>
                    <div className="field-select__search">
                        <FieldInput
                            onInput={onSearchHandler}
                            placeholder="Поиск"
                            className="field-input--no-autofillable"
                        ></FieldInput>
                    </div>

                    <ul className={'field-select__options'}>
                        {!!options.length &&
                            options.map((props) => {
                                const { NAME: text, ID: optionValue, disabled } = props
                                const currentValue = optionValue ?? props
                                const isSelected = isMultiple
                                    ? selectedValues.some((selectedValue) => isSameValue(selectedValue, currentValue))
                                    : isSameValue(currentValue, value)
                                const optionClasses = computedClass({
                                    'field-select__option': true,
                                    'field-select__option--selected': isSelected,
                                    'field-select__option--disabled': disabled,
                                })
                                return (
                                    <li
                                        className={optionClasses}
                                        key={currentValue}
                                        onClick={() => !disabled && onChangeHandler(currentValue)}
                                    >
                                        {text || props}
                                    </li>
                                )
                            })}
                    </ul>
                </div>
            </div>
        </>
    )
}

export default FieldSelect
